<script>
  /**
   * PM-staff review queue.
   *
   * Oldest first, matching the server's ordering — this is a work queue, and
   * the item waiting longest usually has a guest waiting with it.
   */
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Pagination from '../components/Pagination.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);
  let pagination = $state(null);
  let pendingTotal = $state(0);
  let page = $state(1);
  let status = $state('pending');
  let urgency = $state('');
  let undeliveredOnly = $state(false);

  async function load() {
    loading = true;
    error = null;
    try {
      const result = await api.listReview({
        page, limit: 25, status,
        urgency: urgency || undefined,
        undelivered_only: undeliveredOnly || undefined,
        tenant_id: authStore.tenantId ?? undefined,
      });
      items = result.items;
      pagination = result.pagination;
      pendingTotal = result.pending_total;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { page; status; urgency; undeliveredOnly; load(); });

  function go(row) {
    window.history.pushState({}, '', `/strao/review/${row.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  function ago(hours) {
    if (hours == null) return '—';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 48) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  }

  /** Did the host ever actually hear about this item? */
  function undelivered(r) {
    return (r.notify_failure_count ?? 0) > 0 || r.renotify_exhausted_at;
  }

  const columns = [
    {
      key: 'urgency_level', label: '',
      // Emergency items have no draft at all, so they need to be visually
      // distinct in the queue, not just sorted somewhere.
      render: (r) => (r.urgency_level === 'emergency' ? '🚨' : ''),
    },
    { key: 'property_name', label: 'Property', render: (r) => r.property_name ?? 'Unlinked' },
    { key: 'guest_name', label: 'Guest', render: (r) => r.guest_name ?? '—' },
    {
      key: 'original_message_text', label: 'Message',
      render: (r) => {
        const text = r.original_message_text ?? '';
        return text.length > 70 ? `${text.slice(0, 70)}…` : text || '—';
      },
    },
    {
      key: 'response_type', label: 'Why',
      render: (r) => (r.response_type === 'escalated' ? 'Escalated' : 'Low confidence'),
    },
    {
      key: 'confidence_score', label: 'Confidence', align: 'right',
      render: (r) => (r.confidence_score == null ? '—' : `${Math.round(r.confidence_score * 100)}%`),
    },
    { key: 'hours_pending', label: 'Waiting', align: 'right', render: (r) => ago(r.hours_pending) },
    {
      key: 'notify_failure_count', label: 'Notified',
      // An item nobody was told about looks exactly like one nobody has got to
      // yet. Without this the difference is only visible in the logs.
      render: (r) => {
        if ((r.notify_failure_count ?? 0) > 0) return `failed x${r.notify_failure_count}`;
        if (r.renotify_exhausted_at) return 'gave up';
        return '';
      },
    },
  ];
</script>

<PageHeader title="Review queue"
            subtitle="Replies the auto-responder would not send on its own." />

<div class="mb-4 flex flex-wrap items-center gap-3">
  <select class="p-input w-auto" bind:value={status} aria-label="Status filter">
    <option value="pending">Awaiting review</option>
    <option value="reviewed">Already reviewed</option>
    <option value="all">All</option>
  </select>
  <select class="p-input w-auto" bind:value={urgency} aria-label="Urgency filter">
    <option value="">Any urgency</option>
    <option value="emergency">Emergency</option>
    <option value="high">High</option>
    <option value="normal">Normal</option>
  </select>
  <label class="flex items-center gap-2 text-xs p-muted">
    <input type="checkbox" bind:checked={undeliveredOnly} />
    Only items the host was never told about
  </label>
  {#if pendingTotal > 0}
    <span class="text-xs p-faint">
      {pendingTotal} awaiting review in total
    </span>
  {/if}
</div>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if !loading && !items.length}
  <EmptyState icon="fa-circle-check"
              title={status === 'pending' ? 'Nothing waiting' : 'Nothing to show'}
              message={status === 'pending'
                ? 'Every guest message has either been answered automatically or already reviewed.'
                : 'No review items match these filters.'} />
{:else}
  <DataTable {columns} rows={items} {loading} onRowClick={go} />
  <Pagination {pagination} onPage={(p) => (page = p)} />
  <p class="mt-2 text-xs p-faint">Select an item to review it.</p>
{/if}
